import React, { useState, useCallback, useRef, memo } from 'react';
import {
  Plus, Trash2, GripVertical, ChevronDown, ChevronRight,
  FileText, CheckSquare, Paperclip, Cog, FolderOpen,
  Type, Hash, Calendar, List, AlignLeft, CircleDot, Camera,
  X
} from 'lucide-react';
import type {
  ExecutionBlock, ExecutionBlockType,
  GroupBlockConfig, FieldBlockConfig, ChecklistBlockConfig,
  EvidenceBlockConfig, RuleBlockConfig, ExecutionFieldType
} from '../types/card';

// ============================================================================
// PROPS
// ============================================================================

interface ExecutionBlockBuilderProps {
  blocks: ExecutionBlock[];
  onChange: (blocks: ExecutionBlock[]) => void;
  readOnly?: boolean;
  allFieldBlocks?: ExecutionBlock[];
}

// ============================================================================
// HELPERS
// ============================================================================

const generateId = () => `blk-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

const BLOCK_TYPE_META: Record<ExecutionBlockType, { icon: React.ReactNode; label: string; color: string }> = {
  group:     { icon: <FolderOpen size={12} />, label: 'Grupo',     color: 'blue' },
  field:     { icon: <Type size={12} />,       label: 'Campo',     color: 'emerald' },
  checklist: { icon: <CheckSquare size={12} />, label: 'Checklist', color: 'amber' },
  evidence:  { icon: <Paperclip size={12} />,  label: 'Evidência', color: 'purple' },
  rule:      { icon: <Cog size={12} />,        label: 'Regra',     color: 'red' },
};

const FIELD_TYPES: { type: ExecutionFieldType; label: string; icon: React.ReactNode }[] = [
  { type: 'text',       label: 'Texto',       icon: <Type size={11} /> },
  { type: 'number',     label: 'Número',      icon: <Hash size={11} /> },
  { type: 'currency',   label: 'Valor (R$)',   icon: <Hash size={11} /> },
  { type: 'date',       label: 'Data',         icon: <Calendar size={11} /> },
  { type: 'select',     label: 'Seleção',      icon: <List size={11} /> },
  { type: 'textarea',   label: 'Texto Longo',  icon: <AlignLeft size={11} /> },
  { type: 'checkbox',   label: 'Checkbox',     icon: <CheckSquare size={11} /> },
  { type: 'attachment', label: 'Anexo',         icon: <Paperclip size={11} /> },
];

function getDefaultFieldConfig(type: ExecutionFieldType): FieldBlockConfig {
  return { fieldType: type, required: false };
}

function getDefaultConfig(type: ExecutionBlockType): ExecutionBlock['config'] {
  switch (type) {
    case 'group': return { description: '' } as GroupBlockConfig;
    case 'field': return getDefaultFieldConfig('text');
    case 'checklist': return { items: [], requireAll: true, requireOrder: false } as ChecklistBlockConfig;
    case 'evidence': return { requireAttachment: true, requireJustification: false, requirePhoto: false, multiple: false } as EvidenceBlockConfig;
    case 'rule': return { condition: { fieldBlockId: '', operator: 'equals', value: '' }, action: 'show_warning', message: '', type: 'warning' } as RuleBlockConfig;
  }
}

// ============================================================================
// CONFIG EDITORS — defined outside parent to keep stable identity
// ============================================================================

const GroupConfigEditor = memo<{
  block: ExecutionBlock;
  updateBlock: (id: string, u: Partial<ExecutionBlock>) => void;
  readOnly: boolean;
}>(({ block, updateBlock, readOnly }) => {
  const cfg = block.config as GroupBlockConfig;
  return (
    <input
      type="text"
      value={cfg.description ?? ''}
      onChange={(e) => updateBlock(block.id, { config: { ...cfg, description: e.target.value } })}
      readOnly={readOnly}
      className="w-full bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none text-slate-500 dark:text-slate-400"
      placeholder="Descrição do grupo..."
    />
  );
});
GroupConfigEditor.displayName = 'GroupConfigEditor';

const FieldConfigEditor = memo<{
  block: ExecutionBlock;
  updateBlock: (id: string, u: Partial<ExecutionBlock>) => void;
  readOnly: boolean;
}>(({ block, updateBlock, readOnly }) => {
  const cfg = block.config as FieldBlockConfig;
  const updateCfg = (u: Partial<FieldBlockConfig>) => updateBlock(block.id, { config: { ...cfg, ...u } });

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-mono text-slate-400 uppercase">{cfg.fieldType}</span>
        <button
          onClick={() => updateCfg({ required: !cfg.required })}
          className={`text-[10px] font-bold px-1.5 py-0.5 rounded transition-colors ${
            cfg.required
              ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500'
          }`}
        >
          {cfg.required ? 'Obrigatório' : 'Opcional'}
        </button>
      </div>
      {(cfg.fieldType === 'text' || cfg.fieldType === 'textarea') && (
        <input
          type="text"
          value={cfg.placeholder ?? ''}
          onChange={(e) => updateCfg({ placeholder: e.target.value || undefined })}
          readOnly={readOnly}
          className="w-full bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none"
          placeholder="Texto de exemplo para o operador..."
        />
      )}
      {(cfg.fieldType === 'number' || cfg.fieldType === 'currency') && (
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-slate-400">Mín</label>
            <input type="number" value={cfg.validation?.min ?? ''} onChange={(e) => updateCfg({ validation: { ...cfg.validation, min: e.target.value === '' ? undefined : Number(e.target.value) } })} className="w-full bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none" placeholder="—" />
          </div>
          <div className="flex-1">
            <label className="text-[10px] text-slate-400">Máx</label>
            <input type="number" value={cfg.validation?.max ?? ''} onChange={(e) => updateCfg({ validation: { ...cfg.validation, max: e.target.value === '' ? undefined : Number(e.target.value) } })} className="w-full bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none" placeholder="—" />
          </div>
        </div>
      )}
      {cfg.fieldType === 'text' && (
        <div>
          <label className="text-[10px] text-slate-400">Máscara de validação (expressão regular)</label>
          <input type="text" value={cfg.validation?.pattern ?? ''} onChange={(e) => updateCfg({ validation: { ...cfg.validation, pattern: e.target.value || undefined } })} className="w-full bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none font-mono" placeholder="Ex: ^[A-Z]{3}-\d+ (letras maiúsculas + número)" readOnly={readOnly} />
        </div>
      )}
      {cfg.fieldType === 'select' && (
        <div className="space-y-1">
          {(cfg.options ?? []).map((opt: { value: string; label: string }, oidx: number) => (
            <div key={oidx} className="flex items-center gap-1">
              <span className="text-[9px] text-slate-400">{oidx + 1}.</span>
              <input
                type="text"
                value={opt.label}
                onChange={(e) => {
                  const newOpts = [...(cfg.options ?? [])];
                  newOpts[oidx] = { label: e.target.value, value: e.target.value };
                  updateCfg({ options: newOpts });
                }}
                readOnly={readOnly}
                className="flex-1 bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none"
                placeholder="Opção..."
              />
              {!readOnly && (
                <button onClick={() => updateCfg({ options: (cfg.options ?? []).filter((_: { value: string; label: string }, i: number) => i !== oidx) })} className="text-slate-300 hover:text-red-500">
                  <X size={10} />
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <button
              onClick={() => updateCfg({ options: [...(cfg.options ?? []), { value: '', label: '' }] })}
              className="text-[11px] text-blue-500 hover:text-blue-700 font-semibold"
            >
              + Opção
            </button>
          )}
        </div>
      )}
    </div>
  );
});
FieldConfigEditor.displayName = 'FieldConfigEditor';

const ChecklistConfigEditor = memo<{
  block: ExecutionBlock;
  updateBlock: (id: string, u: Partial<ExecutionBlock>) => void;
  readOnly: boolean;
}>(({ block, updateBlock, readOnly }) => {
  const cfg = block.config as ChecklistBlockConfig;
  const updateCfg = (u: Partial<ChecklistBlockConfig>) => updateBlock(block.id, { config: { ...cfg, ...u } });
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const handleItemDrop = (targetIdx: number) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setOverIdx(null); return; }
    const items = [...cfg.items];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(targetIdx, 0, moved);
    updateCfg({ items });
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div className="space-y-1.5">
      <div className="space-y-1">
        {cfg.items.map((item: { id: string; label: string; required: boolean }, idx: number) => (
          <div
            key={item.id}
            className={`flex items-center gap-1.5 group ${dragIdx === idx ? 'opacity-40' : ''} ${overIdx === idx && dragIdx !== idx ? 'ring-1 ring-amber-400 rounded' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleItemDrop(idx); }}
          >
            <GripVertical
              size={10}
              className="text-slate-300 dark:text-slate-600 shrink-0 cursor-grab active:cursor-grabbing"
              draggable={!readOnly}
              onDragStart={(e) => { e.stopPropagation(); setDragIdx(idx); }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
            />
            <input
              type="text"
              value={item.label}
              onChange={(e) => {
                const items = [...cfg.items];
                items[idx] = { ...item, label: e.target.value };
                updateCfg({ items });
              }}
              readOnly={readOnly}
              className="flex-1 bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none"
              placeholder="Item de verificação..."
            />
            <button
              onClick={() => {
                const items = [...cfg.items];
                items[idx] = { ...item, required: !item.required };
                updateCfg({ items });
              }}
              className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded transition-colors ${
                item.required
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500'
              }`}
            >
              {item.required ? 'OBR' : 'OPC'}
            </button>
            {!readOnly && (
              <button onClick={() => updateCfg({ items: cfg.items.filter((i: { id: string }) => i.id !== item.id) })} className="shrink-0 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
      {!readOnly && (
        <button
          onClick={() => updateCfg({ items: [...cfg.items, { id: `chk-${Date.now()}`, label: '', required: true }] })}
          className="text-[11px] font-bold text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 px-2 py-1 rounded flex items-center gap-1 transition-colors"
        >
          <Plus size={10} /> Item
        </button>
      )}
      <div className="flex gap-3">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={cfg.requireAll} onChange={(e) => updateCfg({ requireAll: e.target.checked })} disabled={readOnly} className="rounded border-slate-300 dark:border-slate-600 text-blue-600" />
          <span className="text-[12px] text-slate-500 dark:text-slate-400">Exigir todos</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={cfg.requireOrder} onChange={(e) => updateCfg({ requireOrder: e.target.checked })} disabled={readOnly} className="rounded border-slate-300 dark:border-slate-600 text-blue-600" />
          <span className="text-[12px] text-slate-500 dark:text-slate-400">Ordem obrigatória</span>
        </label>
      </div>
    </div>
  );
});
ChecklistConfigEditor.displayName = 'ChecklistConfigEditor';

const EvidenceConfigEditor = memo<{
  block: ExecutionBlock;
  updateBlock: (id: string, u: Partial<ExecutionBlock>) => void;
  readOnly: boolean;
}>(({ block, updateBlock, readOnly }) => {
  const cfg = block.config as EvidenceBlockConfig;
  const updateCfg = (u: Partial<EvidenceBlockConfig>) => updateBlock(block.id, { config: { ...cfg, ...u } });

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={cfg.requireAttachment} onChange={(e) => updateCfg({ requireAttachment: e.target.checked })} disabled={readOnly} className="rounded border-slate-300 dark:border-slate-600 text-blue-600" />
        <span className="text-[12px] text-slate-500 dark:text-slate-400">Exigir anexo</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={cfg.requireJustification} onChange={(e) => updateCfg({ requireJustification: e.target.checked })} disabled={readOnly} className="rounded border-slate-300 dark:border-slate-600 text-blue-600" />
        <span className="text-[12px] text-slate-500 dark:text-slate-400">Exigir justificativa</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={cfg.requirePhoto} onChange={(e) => updateCfg({ requirePhoto: e.target.checked })} disabled={readOnly} className="rounded border-slate-300 dark:border-slate-600 text-blue-600" />
        <span className="text-[12px] text-slate-500 dark:text-slate-400">Exigir foto</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={cfg.multiple} onChange={(e) => updateCfg({ multiple: e.target.checked })} disabled={readOnly} className="rounded border-slate-300 dark:border-slate-600 text-blue-600" />
        <span className="text-[12px] text-slate-500 dark:text-slate-400">Múltiplos arquivos</span>
      </label>
      {cfg.requireAttachment && (
        <div className="ml-4 space-y-1 border-l-2 border-slate-200 dark:border-slate-600 pl-2">
          <div>
            <label className="text-[10px] text-slate-400">Tipos permitidos</label>
            <input type="text" value={(cfg.allowedTypes ?? []).join(', ')} onChange={(e) => updateCfg({ allowedTypes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} className="w-full bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none" placeholder="pdf, png, jpg" readOnly={readOnly} />
          </div>
          <div>
            <label className="text-[10px] text-slate-400">Tamanho máx. (MB)</label>
            <input type="number" value={cfg.maxSizeMB ?? ''} onChange={(e) => updateCfg({ maxSizeMB: e.target.value === '' ? undefined : Number(e.target.value) })} className="w-20 bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none" placeholder="10" min={1} readOnly={readOnly} />
          </div>
        </div>
      )}
    </div>
  );
});
EvidenceConfigEditor.displayName = 'EvidenceConfigEditor';

const RuleConfigEditor = memo<{
  block: ExecutionBlock;
  allFieldBlocks: ExecutionBlock[];
  updateBlock: (id: string, u: Partial<ExecutionBlock>) => void;
  readOnly: boolean;
}>(({ block, allFieldBlocks, updateBlock, readOnly }) => {
  const cfg = block.config as RuleBlockConfig;
  const updateCfg = (u: Partial<RuleBlockConfig>) => updateBlock(block.id, { config: { ...cfg, ...u } });

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-bold text-slate-400">SE</span>
        <select value={cfg.condition.fieldBlockId} onChange={(e) => updateCfg({ condition: { ...cfg.condition, fieldBlockId: e.target.value } })} className="flex-1 bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none" disabled={readOnly}>
          <option value="">Campo...</option>
          {allFieldBlocks.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <select value={cfg.condition.operator} onChange={(e) => updateCfg({ condition: { ...cfg.condition, operator: e.target.value as any } })} className="bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-1 py-1 rounded focus:outline-none" disabled={readOnly}>
          <option value="equals">=</option>
          <option value="not_equals">≠</option>
          <option value="greater_than">&gt;</option>
          <option value="less_than">&lt;</option>
          <option value="greater_equal">≥</option>
          <option value="less_equal">≤</option>
          <option value="contains">contém</option>
          <option value="is_empty">vazio</option>
          <option value="not_empty">não vazio</option>
        </select>
        <input type="text" value={String(cfg.condition.value ?? '')} onChange={(e) => updateCfg({ condition: { ...cfg.condition, value: e.target.value } })} className="w-16 bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none" placeholder="Valor" readOnly={readOnly} />
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-bold text-slate-400">ENTÃO</span>
        <select value={cfg.action} onChange={(e) => updateCfg({ action: e.target.value as any })} className="flex-1 bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none" disabled={readOnly}>
          <option value="block_completion">Bloquear conclusão</option>
          <option value="require_attachment">Exigir anexo</option>
          <option value="require_field">Exigir campo</option>
          <option value="show_warning">Mostrar alerta</option>
        </select>
      </div>
      <input type="text" value={cfg.message} onChange={(e) => updateCfg({ message: e.target.value })} className="w-full bg-slate-50 dark:bg-slate-700 border-0 text-[12px] px-2 py-1 rounded focus:outline-none" placeholder="Mensagem exibida..." readOnly={readOnly} />
    </div>
  );
});
RuleConfigEditor.displayName = 'RuleConfigEditor';

// ============================================================================
// ADD BLOCK MENU — stable component defined outside parent
// ============================================================================

const AddBlockMenu = memo<{
  parentId: string | null;
  menuKey: string;
  showAddMenu: string | null;
  setShowAddMenu: (v: string | null) => void;
  blocks: ExecutionBlock[];
  onChange: (blocks: ExecutionBlock[]) => void;
  addBlock: (type: ExecutionBlockType, parentId: string | null) => void;
  readOnly: boolean;
}>(({ parentId, menuKey, showAddMenu, setShowAddMenu, blocks, onChange, addBlock, readOnly }) => {
  const isRoot = parentId === null;
  const types: ExecutionBlockType[] = isRoot
    ? ['group']
    : ['field', 'checklist', 'evidence', 'rule'];

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setShowAddMenu(showAddMenu === menuKey ? null : menuKey)}
        className="text-[11px] font-bold text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-2 py-1 rounded flex items-center gap-1 transition-colors border border-blue-100 dark:border-blue-800 hover:border-blue-300"
        disabled={readOnly}
      >
        <Plus size={10} /> Adicionar
      </button>
      {showAddMenu === menuKey && (
        <div className="absolute left-0 top-full mt-1 z-50 w-40 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded shadow-lg py-1">
          {types.map(t => {
            const meta = BLOCK_TYPE_META[t];
            return (
              <button
                key={t}
                onClick={() => t === 'field' ? setShowAddMenu(`${menuKey}:field`) : addBlock(t, parentId)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors text-left"
              >
                <span className={`text-${meta.color}-500`}>{meta.icon}</span>
                {meta.label}
              </button>
            );
          })}
        </div>
      )}
      {showAddMenu === `${menuKey}:field` && (
        <div className="absolute left-0 top-full mt-1 z-50 w-44 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded shadow-lg py-1">
          <button onClick={() => setShowAddMenu(menuKey)} className="w-full flex items-center gap-2 px-3 py-1 text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            ← Voltar
          </button>
          {FIELD_TYPES.map(ft => (
            <button
              key={ft.type}
              onClick={() => {
                const newBlock: ExecutionBlock = {
                  id: generateId(),
                  organizationId: '',
                  stageId: '',
                  type: 'field',
                  label: ft.label,
                  orderIndex: 0,
                  parentId,
                  config: getDefaultFieldConfig(ft.type),
                };
                if (parentId) {
                  const updated = blocks.map(b => {
                    if (b.id === parentId && b.type === 'group') {
                      const children = [...(b.children ?? []), newBlock];
                      children.forEach((c, i) => c.orderIndex = i);
                      return { ...b, children };
                    }
                    return b;
                  });
                  onChange(updated);
                } else {
                  onChange([...blocks, newBlock]);
                }
                setShowAddMenu(null);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors text-left"
            >
              <span className="text-slate-400">{ft.icon}</span>
              {ft.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
AddBlockMenu.displayName = 'AddBlockMenu';

// ============================================================================
// BLOCK CARD — stable component defined outside parent
// ============================================================================

const BlockCard = memo<{
  block: ExecutionBlock;
  index: number;
  total: number;
  parentId: string | null;
  readOnly: boolean;
  collapsed: Set<string>;
  toggleCollapse: (id: string) => void;
  updateBlock: (id: string, u: Partial<ExecutionBlock>) => void;
  removeBlock: (id: string) => void;
  allFieldBlocks: ExecutionBlock[];
  showAddMenu: string | null;
  setShowAddMenu: (v: string | null) => void;
  blocks: ExecutionBlock[];
  onChange: (blocks: ExecutionBlock[]) => void;
  addBlock: (type: ExecutionBlockType, parentId: string | null) => void;
  draggedId: string | null;
  dragOverId: string | null;
  onDragStart: (id: string, parentId: string | null) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDrop: (e: React.DragEvent, targetId: string, parentId: string | null) => void;
  onDragEnd: () => void;
}>(({ block, index, total, parentId, readOnly, collapsed, toggleCollapse, updateBlock, removeBlock, allFieldBlocks, showAddMenu, setShowAddMenu, blocks, onChange, addBlock, draggedId, dragOverId, onDragStart, onDragOver, onDrop, onDragEnd }) => {
  const meta = BLOCK_TYPE_META[block.type];
  const isGroup = block.type === 'group';
  const isCollapsed = collapsed.has(block.id);

  return (
    <div
      className={`rounded border ${isGroup ? 'border-blue-200 dark:border-blue-800' : 'border-slate-200 dark:border-slate-700'} bg-white dark:bg-slate-750 ${draggedId === block.id ? 'opacity-40' : ''} ${dragOverId === block.id && draggedId !== block.id ? 'ring-2 ring-blue-400' : ''}`}
      onDragOver={(e) => onDragOver(e, block.id)}
      onDrop={(e) => onDrop(e, block.id, parentId)}
    >
      {/* Header */}
      <div className={`flex items-center gap-1.5 px-2 py-1.5 min-w-0 ${isGroup ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
        {!readOnly && (
          <GripVertical
            size={10}
            className="text-slate-300 dark:text-slate-600 shrink-0 cursor-grab active:cursor-grabbing"
            draggable
            onDragStart={(e) => { e.stopPropagation(); onDragStart(block.id, parentId); }}
            onDragEnd={onDragEnd}
          />
        )}
        {isGroup && (
          <button onClick={() => toggleCollapse(block.id)} className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
        <span className={`shrink-0 text-${meta.color}-500`}>{meta.icon}</span>
        <input
          type="text"
          value={block.label}
          onChange={(e) => updateBlock(block.id, { label: e.target.value })}
          readOnly={readOnly}
          className="flex-1 min-w-0 bg-transparent border-0 text-[13px] font-semibold text-slate-700 dark:text-slate-200 focus:outline-none placeholder-slate-400 truncate"
          placeholder="Nome do bloco..."
        />
        <span className="shrink-0 text-[10px] font-mono text-slate-400 uppercase">{block.type}</span>
        {!readOnly && (
          <button onClick={() => removeBlock(block.id)} className="shrink-0 text-slate-300 hover:text-red-500 transition-colors">
            <Trash2 size={12} />
          </button>
        )}
      </div>

      {/* Config Editor */}
      {!isCollapsed && (
        <div className="px-2 pb-2 space-y-1.5">
          {block.type === 'group' && <GroupConfigEditor block={block} updateBlock={updateBlock} readOnly={readOnly} />}
          {block.type === 'field' && <FieldConfigEditor block={block} updateBlock={updateBlock} readOnly={readOnly} />}
          {block.type === 'checklist' && <ChecklistConfigEditor block={block} updateBlock={updateBlock} readOnly={readOnly} />}
          {block.type === 'evidence' && <EvidenceConfigEditor block={block} updateBlock={updateBlock} readOnly={readOnly} />}
          {block.type === 'rule' && <RuleConfigEditor block={block} allFieldBlocks={allFieldBlocks} updateBlock={updateBlock} readOnly={readOnly} />}

          {/* Children (for groups) */}
          {isGroup && (
            <div className="mt-2 ml-2 border-l-2 border-blue-100 dark:border-blue-900 pl-2 space-y-2">
              {(block.children ?? []).map((child: ExecutionBlock, ci: number) => (
                <BlockCard
                  key={child.id}
                  block={child}
                  index={ci}
                  total={(block.children ?? []).length}
                  parentId={block.id}
                  readOnly={readOnly}
                  collapsed={collapsed}
                  toggleCollapse={toggleCollapse}
                  updateBlock={updateBlock}
                  removeBlock={removeBlock}
                  allFieldBlocks={allFieldBlocks}
                  showAddMenu={showAddMenu}
                  setShowAddMenu={setShowAddMenu}
                  blocks={blocks}
                  onChange={onChange}
                  addBlock={addBlock}
                  draggedId={draggedId}
                  dragOverId={dragOverId}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onDragEnd={onDragEnd}
                />
              ))}
              {!readOnly && (
                <AddBlockMenu
                  parentId={block.id}
                  menuKey={block.id}
                  showAddMenu={showAddMenu}
                  setShowAddMenu={setShowAddMenu}
                  blocks={blocks}
                  onChange={onChange}
                  addBlock={addBlock}
                  readOnly={readOnly}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
BlockCard.displayName = 'BlockCard';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ExecutionBlockBuilder: React.FC<ExecutionBlockBuilderProps> = ({
  blocks,
  onChange,
  readOnly = false,
}) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [showAddMenu, setShowAddMenu] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const dragParentRef = useRef<string | null>(null);

  const allFieldBlocks = blocks.flatMap(b =>
    b.type === 'group'
      ? (b.children ?? []).filter((c: ExecutionBlock) => c.type === 'field')
      : b.type === 'field' ? [b] : []
  );

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const addBlock = useCallback((type: ExecutionBlockType, parentId: string | null) => {
    const meta = BLOCK_TYPE_META[type];
    const newBlock: ExecutionBlock = {
      id: generateId(),
      organizationId: '',
      stageId: '',
      type,
      label: type === 'group' ? 'Novo Grupo' : meta.label,
      orderIndex: 0,
      parentId,
      config: getDefaultConfig(type),
      children: type === 'group' ? [] : undefined,
    };

    if (parentId) {
      const updated = blocks.map(b => {
        if (b.id === parentId && b.type === 'group') {
          const children = [...(b.children ?? []), newBlock];
          children.forEach((c, i) => c.orderIndex = i);
          return { ...b, children };
        }
        return b;
      });
      onChange(updated);
    } else {
      const updated = [...blocks, newBlock];
      updated.forEach((b, i) => b.orderIndex = i);
      onChange(updated);
    }
    setShowAddMenu(null);
  }, [blocks, onChange]);

  const updateBlock = useCallback((blockId: string, updates: Partial<ExecutionBlock>) => {
    const updateInList = (list: ExecutionBlock[]): ExecutionBlock[] =>
      list.map(b => {
        if (b.id === blockId) return { ...b, ...updates };
        if (b.children) return { ...b, children: updateInList(b.children) };
        return b;
      });
    onChange(updateInList(blocks));
  }, [blocks, onChange]);

  const removeBlock = useCallback((blockId: string) => {
    const removeFromList = (list: ExecutionBlock[]): ExecutionBlock[] =>
      list.filter(b => b.id !== blockId).map(b =>
        b.children ? { ...b, children: removeFromList(b.children) } : b
      );
    onChange(removeFromList(blocks));
  }, [blocks, onChange]);

  const handleDragStart = useCallback((id: string, parentId: string | null) => {
    setDraggedId(id);
    dragParentRef.current = parentId;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(id);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string, targetParentId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedId || draggedId === targetId) { setDraggedId(null); setDragOverId(null); return; }
    const srcParentId = dragParentRef.current;
    // Only reorder within same parent
    if (srcParentId !== targetParentId) { setDraggedId(null); setDragOverId(null); return; }

    const reorder = (list: ExecutionBlock[]): ExecutionBlock[] => {
      const srcIdx = list.findIndex(b => b.id === draggedId);
      const tgtIdx = list.findIndex(b => b.id === targetId);
      if (srcIdx === -1 || tgtIdx === -1) return list;
      const result = [...list];
      const [moved] = result.splice(srcIdx, 1);
      result.splice(tgtIdx, 0, moved);
      result.forEach((b, i) => b.orderIndex = i);
      return result;
    };

    if (srcParentId === null) {
      onChange(reorder(blocks));
    } else {
      onChange(blocks.map(b => {
        if (b.id === srcParentId && b.children) {
          return { ...b, children: reorder(b.children) };
        }
        return b;
      }));
    }
    setDraggedId(null);
    setDragOverId(null);
  }, [draggedId, blocks, onChange]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  return (
    <div className="space-y-2">
      {blocks.length === 0 && (
        <p className="text-[12px] text-slate-400 dark:text-slate-500 italic text-center py-4 border border-dashed border-slate-200 dark:border-slate-700 rounded">
          Nenhum bloco configurado. Adicione um grupo para começar.
        </p>
      )}
      {blocks.map((block, idx) => (
        <BlockCard
          key={block.id}
          block={block}
          index={idx}
          total={blocks.length}
          parentId={null}
          readOnly={readOnly}
          collapsed={collapsed}
          toggleCollapse={toggleCollapse}
          updateBlock={updateBlock}
          removeBlock={removeBlock}
          allFieldBlocks={allFieldBlocks}
          showAddMenu={showAddMenu}
          setShowAddMenu={setShowAddMenu}
          blocks={blocks}
          onChange={onChange}
          addBlock={addBlock}
          draggedId={draggedId}
          dragOverId={dragOverId}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
        />
      ))}
      {!readOnly && (
        <AddBlockMenu
          parentId={null}
          menuKey="__root__"
          showAddMenu={showAddMenu}
          setShowAddMenu={setShowAddMenu}
          blocks={blocks}
          onChange={onChange}
          addBlock={addBlock}
          readOnly={readOnly}
        />
      )}

      {/* Summary */}
      {blocks.length > 0 && (
        <div className="bg-slate-100 dark:bg-slate-750 rounded p-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">Resumo</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]">
            <span className="text-slate-400">Grupos</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">{blocks.filter(b => b.type === 'group').length}</span>
            <span className="text-slate-400">Campos</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {blocks.flatMap(b => b.type === 'group' ? (b.children ?? []) : [b]).filter(b => b.type === 'field').length}
            </span>
            <span className="text-slate-400">Checklists</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {blocks.flatMap(b => b.type === 'group' ? (b.children ?? []) : [b]).filter(b => b.type === 'checklist').length}
            </span>
            <span className="text-slate-400">Regras</span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {blocks.flatMap(b => b.type === 'group' ? (b.children ?? []) : [b]).filter(b => b.type === 'rule').length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
